import airtableUtils from "./airtableUtils";
import simulationService from "./simulation";
import { useState, useEffect, useCallback, useRef } from "react";

// Global request cache to prevent duplicate API calls across all hook instances
const requestCache = new Map();

// Hook for managing multi-day schedule data
export const useMultiDaySchedule = (
  initialStartDate,
  initialEndDate,
  maxDays = null,
  clinicId = null,
  demoMode = false,
  session = null,
  useDummyData = false,
) => {
  // State for schedule data, organized by date
  const [scheduleData, setScheduleData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentDaysToFetch, setCurrentDaysToFetch] = useState(daysToFetch);

  // Memoize the date values to prevent unnecessary re-renders
  const [startDate] = useState(initialStartDate);
  const [endDate] = useState(initialEndDate);
  const [daysToFetch] = useState(maxDays);

  const updateViewMode = (newDaysToFetch) => {
    setCurrentDaysToFetch(newDaysToFetch);
    // This will trigger a refresh with the new days value
    refreshSchedule();
  };

  // Function to fetch schedule data for multiple days
  const fetchDateRangeSchedule = useCallback(
    async (start, end) => {
      // Apply daysToFetch logic to modify date range if needed
      let effectiveStart = start;
      let effectiveEnd = end;

      // Only override when an explicit range was not supplied
      if (!effectiveStart || !effectiveEnd) {
        if (currentDaysToFetch === 1) {
          // Just get today's date in YYYY-MM-DD format
          const today = new Date().toISOString().split("T")[0];
          effectiveStart = today;
          effectiveEnd = today;
        } else if (currentDaysToFetch !== null) {
          // Get today's date
          const today = new Date();

          // Calculate half of maxDays (floored) for days before today
          const daysBefore = Math.floor(daysToFetch / 2);
          // Calculate remaining days for after today
          const daysAfter = daysToFetch - daysBefore - 1; // -1 because today is counted

          // Calculate new start date (daysBefore days before today)
          const newStart = new Date(today);
          newStart.setDate(today.getDate() - daysBefore);
          effectiveStart = newStart.toISOString().split("T")[0];

          // Calculate new end date (daysAfter days after today)
          const newEnd = new Date(today);
          newEnd.setDate(today.getDate() + daysAfter);
          effectiveEnd = newEnd.toISOString().split("T")[0];
        }
      }

      // Create cache key
      const cacheKey = `${effectiveStart}|${effectiveEnd}|${clinicId}|${useDummyData}`;

      // Check if there's an in-flight request for this exact same data
      if (requestCache.has(cacheKey)) {
        console.log(`♻️ Reusing in-flight request for ${cacheKey}`);
        return requestCache.get(cacheKey);
      }

      console.log(
        `🔎 Making API call: ${effectiveStart} to ${effectiveEnd} (dummy: ${useDummyData})`,
      );

      // Create the promise and cache it
      const requestPromise = airtableUtils
        .fetchDateRangeScheduleClinicOS(
          effectiveStart,
          effectiveEnd,
          clinicId,
          session,
          useDummyData,
        )
        .then((data) => {
          // Clear from cache after successful completion
          requestCache.delete(cacheKey);
          return data;
        })
        .catch((err) => {
          // Clear from cache on error so retries can happen
          requestCache.delete(cacheKey);
          console.error("Error fetching date range schedule:", err);
          throw err;
        });

      // Store in cache
      requestCache.set(cacheKey, requestPromise);

      return requestPromise;
    },
    [currentDaysToFetch, daysToFetch, clinicId, session, useDummyData],
  );

  // Load initial data on mount
  useEffect(() => {
    let isMounted = true;

    const loadInitialData = async () => {
      if (!isMounted) return;

      setLoading(true);

      try {
        const data = await fetchDateRangeSchedule(startDate, endDate);
        if (isMounted) {
          setScheduleData(data);
          setError(null);
        }
      } catch (err) {
        console.error("Error loading initial schedule data:", err);
        if (isMounted) {
          setError(err);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadInitialData();

    return () => {
      isMounted = false;
    };
  }, [startDate, endDate, clinicId, useDummyData, fetchDateRangeSchedule]);

  // Function to refresh schedule data
  const refreshSchedule = useCallback(
    async (newStartDate = null, newEndDate = null, mockData = null) => {
      const effectiveStartDate = newStartDate || startDate;
      const effectiveEndDate = newEndDate || endDate;

      setLoading(true);

      try {
        const data = await fetchDateRangeSchedule(
          effectiveStartDate,
          effectiveEndDate,
        );
        setScheduleData(data);
        setError(null);
      } catch (err) {
        console.error("Error refreshing schedule data:", err);
        setError(err);
      } finally {
        setLoading(false);
      }
    },
    [startDate, endDate, fetchDateRangeSchedule],
  );

  // Function to load more days (past or future)
  const loadMoreDays = useCallback(
    async (direction, daysToAdd) => {
      // If we're limiting to just today or a fixed number of days, don't actually load more
      if (daysToFetch !== null) {
        return;
      }

      try {
        let newStartDate = startDate;
        let newEndDate = endDate;

        if (direction === "past") {
          // Load more days in the past
          const tempDate = new Date(startDate);
          tempDate.setDate(tempDate.getDate() - daysToAdd);
          newStartDate = tempDate.toISOString().split("T")[0];

          try {
            // Try fetching the data
            const newData = await fetchDateRangeSchedule(
              newStartDate,
              startDate,
            );

            // Update state, but don't try to use setStartDate
            setScheduleData((prevData) => [...newData, ...prevData]);
          } catch (apiErr) {
            console.error(`Error fetching past days: ${apiErr.message}`);
          }
        } else if (direction === "future") {
          // Similar code for loading future days
          // ...
        }
      } catch (err) {
        console.error(`Error loading more ${direction} days:`, err);
        setError(err);
      }
    },
    [
      startDate,
      endDate,
      fetchDateRangeSchedule,
      daysToFetch,
      session,
      useDummyData,
    ],
  );

  // Function to search patients across all dates
  const searchPatients = useCallback(
    (searchTerm) => {
      if (!searchTerm || searchTerm.trim() === "") {
        return scheduleData;
      }

      // Filter patients based on search term
      return scheduleData
        .map((section) => {
          const filteredData = section.data.filter(
            (patient) =>
              patient.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
              (patient.procedureType &&
                patient.procedureType
                  .toLowerCase()
                  .includes(searchTerm.toLowerCase())),
          );

          if (filteredData.length === 0) {
            return null;
          }

          return {
            ...section,
            data: filteredData,
          };
        })
        .filter(Boolean); // Remove null sections
    },
    [scheduleData, session],
  );

  return {
    scheduleData,
    loading,
    error,
    refreshSchedule,
    loadMoreDays,
    searchPatients,
    updateViewMode,
  };
};

export const useSimulations = (patient) => {
  const [simulations, setSimulations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingStars] = useState(false);
  const [error, setError] = useState(null);
  const [starError] = useState(null);

  // Load simulations from patient payload only.
  useEffect(() => {
    if (!patient) {
      setSimulations([]);
      return;
    }

    const loadSimulations = async () => {
      setLoading(true);
      setError(null);

      try {
        let simulationsToUse = [];

        // Check for pre-fetched or raw simulations
        if (patient.simulations?.length > 0) {
          simulationsToUse = patient.simulations;
        } else if (patient.rawFields?.Simulations) {
          simulationsToUse =
            simulationService.extractSimulationsFromPatient(patient);
        } else {
          console.log("No pre-generated simulations found for this patient");
        }

        // Normalize star flag from payload when present.
        const normalizedSimulations = simulationsToUse.map((simulation) => ({
          ...simulation,
          isStarred: Boolean(simulation.isStarred),
        }));

        setSimulations(normalizedSimulations);
      } catch (err) {
        console.error("Error loading simulations:", err);
        setError(err.message || "Failed to load simulations");
        setSimulations([]);
      } finally {
        setLoading(false);
      }
    };

    loadSimulations();
  }, [patient]);

  const starredSimulations = simulations
    .filter((simulation) => simulation.isStarred)
    .map((simulation) => simulation.id);

  // No-op while legacy Airtable star persistence is removed.
  const toggleSimulationStar = useCallback(() => {
    console.warn(
      "toggleSimulationStar in useAirtable is deprecated and currently disabled.",
    );
  }, []);

  return {
    simulations,
    loading,
    loadingStars,
    error,
    starError,
    starredSimulations,
    toggleSimulationStar,
  };
};

// Export all hooks as named exports
export default {
  useMultiDaySchedule,
  useSimulations,
};
